import React, {
	useState, useEffect, useRef, useContext,
} from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { streamData } from '../helpers/methods';
import { CustomerContext } from '../contexts/customer-context';
import { UserContext } from '../contexts/user-context';

import AiButtonSearch from './buttons/ai-button-search';
import AiButtonArrow from './buttons/ai-button-arrow';
import AiButtonClose from './buttons/ai-button-close';
import AiLoadingBar from './loading/ai-loading-bar';
import AiRating from './search/ai-rating';
import AiDebug from './search/ai-debug';
import AiResultCard from './search/ai-result-card';
import AiQuery from './general/ai-query';

const IgniteAiSearch = ({
	handleClose,
	isOpen,
	userName = '',
}) => {
	const navigate = useNavigate();
	const inputRef = useRef(null);
	const {
		claims: userDataRoles = [],
		preferredUsername,
	} = useContext(UserContext);
	const {
		portalAdminUIConfig: {
			aiAccess: aiAccessUIConfig = {},
		} = {},
		portalAIConfig: {
			searchPrompts = [],
		} = {},
	} = useContext(CustomerContext);
	const [loading, setLoading] = useState(false);
	const [animateAsk, setAnimateAsk] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [disableNewSearch, setDisableNewSearch] = useState(false);
	const [question, setQuestion] = useState('');
	const [buildResponse, setBuildResponse] = useState('');
	const [vectors, setVectors] = useState([]);
	const [results, setResults] = useState([]);
	const [naturalLanguageResponse, setNaturalLanguageResponse] = useState('');
	const [feedbackKey, setFeedbackKey] = useState('');

	const checkAiPermissions = () => {
		const allowedRoles = aiAccessUIConfig?.debugRoles || [];
		const hasAllowedRole = userDataRoles.some(r => allowedRoles.includes(r));
		const allowedUsers = aiAccessUIConfig?.debugUsers || [];
		const isAllowedUser = allowedUsers.includes(preferredUsername.toLowerCase());

		if (hasAllowedRole || isAllowedUser) {
			return true;
		}
		return false;
	};

	const aiDebugAccess = checkAiPermissions();

	const handleModalContainerClick = e => {
		e.stopPropagation();
	};

	const getUserName = () => {
		if (userName.includes('@') || userName === 'admin' || userName === '') {
			return 'there';
		}
		return userName;
	};

	const handleNewSearch = () => {
		setAnimateAsk(false);
		setQuestion('');
		setInputValue('');
		setVectors([]);
		setBuildResponse('');
		setResults([]);
		setNaturalLanguageResponse('');
		setFeedbackKey('');
	};

	const streamCallback = chunkStr => {
		const parts = chunkStr.split(/\r?\n/);
		for (let i = 0; i < parts.length; i += 1) {
			if (parts[i].length !== 0) {
				const parsedData = JSON.parse(parts[i]);

				if (parsedData.type === 'VectorOut') {
					setVectors(parsedData.vectorOverviews);
				}

				if (parsedData.type === 'SearchParamsOut') {
					setBuildResponse(parsedData.buildResponse);
				}

				if (parsedData.type === 'JsonContentOut') {
					setResults(parsedData.content.results);
				}

				if (parsedData.type === 'RenderTextOut') {
					const parsedResponse = JSON.parse(parsedData.response);
					setNaturalLanguageResponse(parsedResponse.natural_language_response);
				}

				if (parsedData.type === 'FeedbackOut') {
					setFeedbackKey(parsedData.key);
					setLoading(false);
					setDisableNewSearch(false);
				}
			}
		}
	};

	const getStreamData = () => {
		setDisableNewSearch(true);
		setAnimateAsk(true);
		setQuestion(inputValue);
		setLoading(true);

		const uri = `/api/web/chats/stream/renderedSearch?userQuery=${inputValue}`;
		streamData({
			uri,
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
			callback: streamCallback,
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter') {
			e.preventDefault();
			getStreamData();
		}
	};

	const handleVectorClick = id => {
		navigate(`/catalog/details/${id}`);
		handleClose();
	};

	useEffect(() => {
		if (isOpen && !question) {
			inputRef.current.focus();
		}
	}, [isOpen]);

	useEffect(() => {
		if (question === '') {
			inputRef.current.focus();
		}
	}, [question]);

	return (
		<>
			<div
				className={`ignite-ai__modal-search ${isOpen && 'is-open'}`}
				role="presentation"
				onClick={handleModalContainerClick}
			>
				<div className="ignite-ai__modal-title-container">
					<div className="flex-row justify-flex-start align-items-center">
						<AiButtonSearch
							action={() => handleNewSearch()}
							title="New Search"
							isDisabled={disableNewSearch}
						/>
						<div className="ignite-ai__beta-flag">BETA</div>
					</div>
					<AiButtonClose
						action={() => handleClose()}
					/>
				</div>
				<div className="ignite-ai__modal-child-container">
					{!question && (
						<div className={`ignite-ai__search-question-container ${animateAsk && 'animate-close'}`}>
							<div className="ignite-ai__search-question-title">
								Hi
								{' '}
								{getUserName()}
								, nice to see you.
							</div>
							<div className="ignite-ai__text-entry-outline">
								<div className="ignite-ai__text-entry-container flex-column justify-space-between">
									<textarea
										className="ignite-ai__text-entry-input"
										id="ai-search-question"
										name="ai-search-question"
										onChange={e => setInputValue(e.target.value)}
										onKeyPress={() => {}}
										placeholder="What can I help you find today?"
										type="textarea"
										value={inputValue}
										onKeyDown={handleKeyDown}
										ref={inputRef}
									/>
									<div className="ignite-ai__button-container flex-row justify-flex-end">
										<AiButtonArrow
											action={() => getStreamData()}
											isDisabled={inputValue?.length < 1}
											title="Send"
										/>
									</div>
								</div>
							</div>
							{searchPrompts.length > 0 && (
								<div className="ignite-ai__search-example-container">
									<div className="ignite-ai__search-example-title">
										Or try one of these prompt suggestions
									</div>
									<div className="ignite-ai__search-example-item-container">
										{searchPrompts.map((prompt, index) => (
											<div
												// eslint-disable-next-line react/no-array-index-key
												key={index}
												className="ignite-ai__card-example-prompt"
												onClick={() => setInputValue(prompt)}
												role="presentation"
											>
												&ldquo;
												{prompt}
												&rdquo;
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					)}
					{question && (
						<AiQuery
							query={question}
						/>
					)}
					{vectors.length > 0 && (
						<p className="ignite-ai__general-text ignite-ai__animate-in-1">
							I have identified some key resources,
							{' '}
							{vectors.map((item, index) => (
								<>
									<span
										// eslint-disable-next-line react/no-array-index-key
										key={index}
										onClick={() => handleVectorClick(item.id)}
										role="presentation"
										className="ignite-ai__search-vector-result"
									>
										{item.details}
									</span>
									{vectors.length === (index + 1) ? (
										<>.</>
									) : (
										<>, </>
									)}
								</>
							))}
						</p>
					)}
					{buildResponse.length > 0 && (
						<>
							{buildResponse?.length > 0 && (
								<p className="ignite-ai__general-text ignite-ai__animate-in-1">
									{buildResponse}
								</p>
							)}
							{results?.length > 0 && (
								<div className="ignite-ai__search-result-container ignite-ai__animate-in-1 flex-column">
									{results.map((item, index) => (
										<AiResultCard
											// eslint-disable-next-line react/no-array-index-key
											key={index}
											data={item}
											handleClose={handleClose}
										/>
									))}
								</div>
							)}
							{/* eslint-disable-next-line camelcase */}
							{naturalLanguageResponse?.length > 0 && (
								<p className="ignite-ai__general-text ignite-ai__animate-in-1">
									{naturalLanguageResponse}
								</p>
							)}
							{feedbackKey?.length > 0 && (
								<div className="ignite-ai__animate-in-2 flex-row justify-space-between">
									<AiRating
										feedbackKey={feedbackKey}
									/>
									{aiDebugAccess && (
										<AiDebug
											feedbackKey={feedbackKey}
										/>
									)}
								</div>
							)}
						</>
					)}
					{loading && (
						<AiLoadingBar />
					)}
				</div>
				<div className="ignite-ai__top-fade" />
				<div className="ignite-ai__bottom-fade" />
			</div>
			<div className={`ignite-ai__modal-background-search ${!isOpen && 'ignite-ai__display-none'}`} role="presentation" onClick={handleClose}> </div>
		</>
	);
};

export default IgniteAiSearch;

IgniteAiSearch.propTypes = {
	handleClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool.isRequired,
	userName: PropTypes.string,
};
